if (typeof ProductCardCarousel !== 'function') {
  window.ProductCardCarousel = class extends HTMLElement {
    constructor() {
      super();
      this.hoverActive = false;
      this.activeIndex = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-pcc-track]');
      this.slides = this.track ? Array.from(this.track.querySelectorAll('[data-pcc-slide]')) : [];
      this.progress = this.querySelector('[data-pcc-progress]');
      this.prevBtn = this.querySelector('[data-pcc-prev]');
      this.nextBtn = this.querySelector('[data-pcc-next]');

      if (!this.track || this.slides.length < 2) return;

      this.isTouch = matchMedia('(hover: none)').matches;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.labelSlides();
      this.buildProgress();
      this.observeSlides();
      this.bindControls();
      this.bindHover();
      this.updateNavState();
      this.setupViewportLoad();
      this.setupInteractLoad();
      this.setupAxisLock();
      this.setupSnapWatchdog();
      this.setupPeek();
    }

    setupAxisLock() {
      // First few pixels of touch movement decide axis. Vertical intent
      // releases the track so the page scrolls; horizontal stays with the
      // carousel. 30° off-axis threshold, matching native carousel UX.
      const MOVE_THRESHOLD = 6;
      const ANGLE_TAN = Math.tan((30 * Math.PI) / 180);
      let startX = 0;
      let startY = 0;
      let axisDecided = false;

      const reset = () => {
        axisDecided = false;
        this.track.classList.remove('is-axis-locked-vertical');
      };

      this.track.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        axisDecided = false;
        this.track.classList.remove('is-axis-locked-vertical');
      }, { passive: true });

      this.track.addEventListener('touchmove', (e) => {
        if (axisDecided) return;
        const t = e.touches[0];
        const dx = Math.abs(t.clientX - startX);
        const dy = Math.abs(t.clientY - startY);
        if (dx < MOVE_THRESHOLD && dy < MOVE_THRESHOLD) return;
        axisDecided = true;
        if (dy > dx * ANGLE_TAN) {
          this.track.classList.add('is-axis-locked-vertical');
        }
      }, { passive: true });

      this.track.addEventListener('touchend', reset, { passive: true });
      this.track.addEventListener('touchcancel', reset, { passive: true });
    }

    setupSnapWatchdog() {
      // Catches iOS Safari cases where a swipe releases between snap points
      // and the native snap engine fails to resolve.
      const SETTLE_MS = 140;
      const TOLERANCE_PX = 2;
      let timer = null;
      const settle = () => {
        if (this._peeking || this._snapping) return;
        const w = this.track.clientWidth;
        if (!w) return;
        const idx = Math.max(0, Math.min(Math.round(this.track.scrollLeft / w), this.slides.length - 1));
        const target = this.slides[idx];
        if (!target) return;
        if (Math.abs(this.track.scrollLeft - target.offsetLeft) <= TOLERANCE_PX) return;
        this._snapping = true;
        this.track.scrollTo({ left: target.offsetLeft, behavior: this.reducedMotion ? 'auto' : 'smooth' });
        setTimeout(() => { this._snapping = false; }, 300);
      };
      this.track.addEventListener('scroll', () => {
        clearTimeout(timer);
        timer = setTimeout(settle, SETTLE_MS);
      }, { passive: true });
      this.track.addEventListener('touchend', () => {
        clearTimeout(timer);
        timer = setTimeout(settle, SETTLE_MS);
      }, { passive: true });
    }

    hydrateImage(img) {
      const src = img.dataset.src;
      const srcset = img.dataset.srcset;
      if (src) {
        img.src = src;
        delete img.dataset.src;
      }
      if (srcset) {
        img.srcset = srcset;
        delete img.dataset.srcset;
      }
      img.removeAttribute('data-pcc-load');
    }

    setupViewportLoad() {
      const targets = Array.from(this.querySelectorAll('img[data-pcc-load="viewport"]'));
      if (!targets.length) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          targets.forEach((img) => this.hydrateImage(img));
        });
      });
      io.observe(this);
      this.viewportLoadIO = io;
    }

    setupInteractLoad() {
      const targets = Array.from(this.querySelectorAll('img[data-pcc-load="interact"]'));
      if (!targets.length) return;
      const ac = new AbortController();
      const trigger = () => {
        ac.abort();
        targets.forEach((img) => this.hydrateImage(img));
      };
      const opts = { signal: ac.signal, once: true, passive: true };
      this.addEventListener('mouseenter', trigger, opts);
      this.addEventListener('touchstart', trigger, opts);
      this.addEventListener('focusin', trigger, opts);
      this.addEventListener('wheel', trigger, opts);
      this.interactLoadController = ac;

      // If the carousel was swapped in under an already-hovered cursor
      // (swatch change), the mouseenter we just bound will never fire.
      if (this.matches(':hover')) trigger();
    }

    labelSlides() {
      if (!this.track.hasAttribute('role')) this.track.setAttribute('role', 'group');
      const total = this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${i + 1} of ${total}`);
      });
    }

    setupPeek() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (document.querySelector('product-card-carousel') !== this) return;

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio < 0.5) return;
            io.disconnect();
            if (this.hoverActive) return;
            this.runPeek();
          });
        },
        { threshold: 0.5 }
      );
      io.observe(this);
      this.peekIO = io;
      this.track.addEventListener('touchstart', () => this.cancelPeek(), { passive: true, once: true });
    }

    runPeek() {
      this._peeking = true;
      this.track.style.scrollSnapType = 'none';

      const peekDist = Math.round(this.track.clientWidth * 0.55);
      const outDuration = 550;
      const inDuration = 500;
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const smoothstep = (t) => t * t * (3 - 2 * t);

      let phaseStart = null;
      let phase = 'out';

      const step = (now) => {
        if (!this._peeking) return;
        if (phaseStart === null) phaseStart = now;
        const elapsed = now - phaseStart;

        if (phase === 'out') {
          const t = Math.min(elapsed / outDuration, 1);
          this.track.scrollLeft = peekDist * easeOutCubic(t);
          if (t >= 1) {
            phase = 'in';
            phaseStart = null;
          }
          requestAnimationFrame(step);
        } else {
          const t = Math.min(elapsed / inDuration, 1);
          this.track.scrollLeft = peekDist * (1 - smoothstep(t));
          if (t < 1) {
            requestAnimationFrame(step);
          } else {
            this.track.scrollLeft = 0;
            this.track.style.scrollSnapType = '';
            this._peeking = false;
          }
        }
      };
      requestAnimationFrame(step);
    }

    cancelPeek() {
      if (!this._peeking) return;
      this._peeking = false;
      this.track.style.scrollSnapType = '';
    }

    buildProgress() {
      if (!this.progress) return;
      this.progress.innerHTML = '';
      this.segments = this.slides.map((_, i) => {
        const seg = document.createElement('span');
        seg.className = 'product-card-carousel__progress-segment';
        if (i === 0) seg.classList.add('is-active');
        this.progress.appendChild(seg);
        return seg;
      });
    }

    observeSlides() {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
            const idx = this.slides.indexOf(entry.target);
            if (idx === -1 || idx === this.activeIndex) return;
            this.activeIndex = idx;
            this.setActive(idx);
            this.updateNavState();
          });
        },
        { root: this.track, threshold: [0.6] }
      );
      this.slides.forEach((slide) => io.observe(slide));
      this.io = io;
    }

    setActive(idx) {
      if (!this.segments) return;
      this.segments.forEach((seg, i) => {
        seg.classList.toggle('is-active', i === idx);
      });
    }

    bindControls() {
      const scrollByOne = (dir) => {
        const target = this.slides[this.activeIndex + dir];
        if (!target) return;
        const behavior = this.reducedMotion ? 'auto' : 'smooth';
        target.scrollIntoView({ behavior, inline: 'start', block: 'nearest' });
      };

      this.prevBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        scrollByOne(-1);
      });
      this.nextBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        scrollByOne(1);
      });
    }

    updateNavState() {
      if (this.prevBtn) this.prevBtn.toggleAttribute('hidden', this.activeIndex === 0);
      if (this.nextBtn) this.nextBtn.toggleAttribute('hidden', this.activeIndex === this.slides.length - 1);
    }

    jumpTo(idx) {
      const target = this.slides[idx];
      if (!target) return;
      if (this._snapRestore) cancelAnimationFrame(this._snapRestore);
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = target.offsetLeft;
      this._snapRestore = requestAnimationFrame(() => {
        this.track.style.scrollSnapType = '';
        this._snapRestore = null;
      });
    }

    bindHover() {
      if (this.isTouch) return;

      this.addEventListener('mouseenter', () => {
        this.hoverActive = true;
        this.cancelPeek();
        this.jumpTo(1);
      });

      this.addEventListener('mouseleave', () => {
        this.hoverActive = false;
        this.jumpTo(0);
      });
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.peekIO?.disconnect();
      this.viewportLoadIO?.disconnect();
      this.interactLoadController?.abort();
    }
  };

  customElements.define('product-card-carousel', window.ProductCardCarousel);
}
